import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface SharedItem {
  id: string;
  sender_id: string;
  recipient_id: string;
  saved_item_id: string;
  message: string | null;
  is_read: boolean;
  created_at: string;
  sender_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
  saved_item?: {
    name: string | null;
    image_url: string | null;
  };
}

export interface ShareItemInput {
  recipient_id: string;
  saved_item_id?: string;
  poll_id?: string;
  look_id?: string;
  collection_id?: string;
  body_gym_look_id?: string;
  message?: string;
}

export function useSharedItems() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch items shared with the current user
  const { data: receivedShares, isLoading: isLoadingReceived } = useQuery({
    queryKey: ['shared-items', 'received', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('shared_items')
        .select('*')
        .eq('recipient_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SharedItem[];
    },
    enabled: !!user,
  });

  // Fetch items the current user has shared
  const { data: sentShares, isLoading: isLoadingSent } = useQuery({
    queryKey: ['shared-items', 'sent', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('shared_items')
        .select('*')
        .eq('sender_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SharedItem[];
    },
    enabled: !!user,
  });

  // Share an item or poll with a friend
  const shareItem = useMutation({
    mutationFn: async (input: ShareItemInput) => {
      if (!user) throw new Error('Must be logged in to share');
      if (!input.saved_item_id && !input.poll_id && !input.look_id && !input.collection_id && !input.body_gym_look_id) {
        throw new Error('Either saved_item_id, poll_id, look_id, collection_id, or body_gym_look_id must be provided');
      }
      
      const { data, error } = await supabase
        .from('shared_items')
        .insert({
          sender_id: user.id,
          recipient_id: input.recipient_id,
          saved_item_id: input.saved_item_id || null,
          poll_id: input.poll_id || null,
          look_id: input.look_id || null,
          collection_id: input.collection_id || null,
          body_gym_look_id: input.body_gym_look_id || null,
          message: input.message || null,
        } as any)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-items'] });
    },
  });

  // Mark a shared item as read
  const markAsRead = useMutation({
    mutationFn: async (shareId: string) => {
      const { error } = await supabase
        .from('shared_items')
        .update({ is_read: true })
        .eq('id', shareId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-items'] });
    },
  });

  return {
    receivedShares: receivedShares || [],
    sentShares: sentShares || [],
    isLoading: isLoadingReceived || isLoadingSent,
    shareItem,
    markAsRead,
  };
}
